import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import Searchbar from "../../common/searchbar";
import DataTable from "../../common/dataTable";
import TablePagination from "../../common/tablePagination";
import MauvelousPrimaryButton from "../../common/button/mauvelousPrimaryButton";
import { itemsPerPage } from "../../utils/constants";
import { cssProperties } from "../../utils/commonCssProperties";
import commonActions from "../../redux/common/actions";
import examScheduleActions from "../../redux/examSchedule/actions";
import SectionRowModal from "./modals/sectionRowModal";
import PaperRowModal from "./modals/paperRowModal";
import DeleteModal from "./modals/deleteModal";
import PreviewPanel from "./previewPanel";

const sectionKeys = [
  { name: "SI.NO", accessor: "slno", sortOption: false },
  { name: "Section", accessor: "section_no", sortOption: false },
  { name: "Section Title", accessor: "section_title", sortOption: false },
  { name: "Row", accessor: "row_no", sortOption: false },
  { name: "Label", accessor: "label", sortOption: false },
  { name: "Date", accessor: "examdate", sortOption: false },
  { name: "Time", accessor: "time", sortOption: false },
  { name: "Note", accessor: "note", sortOption: false },
  { name: "Weekday", accessor: "weekday", sortOption: false },
  { name: "Instruction", accessor: "instruction", sortOption: false },
];

const paperKeys = [
  { name: "SI.NO", accessor: "slno", sortOption: false },
  { name: "Paper No", accessor: "paperno", sortOption: false },
  { name: "Label", accessor: "paperlabel", sortOption: false },
  { name: "Name", accessor: "papername", sortOption: false },
  { name: "Date", accessor: "examdate", sortOption: false },
  { name: "Reporting", accessor: "reportingtime", sortOption: false },
  { name: "Gate Closing", accessor: "gateclosingtime", sortOption: false },
  { name: "Schedule", accessor: "time", sortOption: false },
  { name: "Marks", accessor: "marks", sortOption: false },
  { name: "Duration", accessor: "durationtext", sortOption: false },
];

const timeRange = (start, end) => {
  if (!start) return "";
  return end ? `${start} - ${end}` : start;
};

export default function Index() {
  const dispatch = useDispatch();
  const { scheduleCourses, sectionRows, paperRows, schedulePreview } = useSelector(
    (state) => state.examScheduleReducer
  );

  const [courseid, setCourseid] = useState(0);
  const [tab, setTab] = useState("section");
  const [searchTerm, setSearchTerm] = useState("");
  const [itemOffset, setItemOffset] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    dispatch({ type: examScheduleActions.GET_SCHEDULE_COURSES });
  }, [dispatch]);

  // courseid is a number here but a string on the rows, so every lookup goes
  // through Number() rather than a strict compare
  const selectedCourse = useMemo(
    () => scheduleCourses?.find((course) => Number(course?.courseid) === Number(courseid)),
    [scheduleCourses, courseid]
  );

  const usesPaperGrid = !!selectedCourse?.usesPaperGrid;
  const isPaperTab = tab === "paper" && usesPaperGrid;

  // DataTable prints val[accessor] straight through, and React renders a boolean
  // as nothing, so the display strings are built here
  const rows = useMemo(() => {
    if (isPaperTab) {
      return paperRows?.map((row) => ({
        ...row,
        paperno: Number(row?.paper_no) === 0 ? "0 (break)" : row?.paper_no,
        time: timeRange(row?.starttime, row?.endtime),
        durationtext: row?.duration ? `${row?.duration} min` : "",
      }));
    }
    return sectionRows?.map((row) => ({
      ...row,
      time: timeRange(row?.starttime, row?.endtime),
      weekday: row?.show_weekday ? "Yes" : "No",
      instruction: row?.is_instruction ? "Yes" : "No",
    }));
  }, [isPaperTab, sectionRows, paperRows]);

  const filteredItems = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return rows?.filter((row) =>
      (isPaperTab ? row?.papername : row?.label)?.toLowerCase()?.includes(term)
    );
  }, [rows, searchTerm, isPaperTab]);

  const endOffset = itemOffset + itemsPerPage;
  const currentItems = filteredItems
    ?.slice(itemOffset, endOffset)
    ?.map((row, i) => ({ ...row, slno: itemOffset + i + 1 }));
  const pageCount = Math.ceil(filteredItems?.length / itemsPerPage);

  const resetPaging = () => {
    setItemOffset(0);
    setCurrentPage(1);
  };

  const handlePageChange = (event, value) => {
    setItemOffset((value - 1) * itemsPerPage);
    setCurrentPage(value);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    resetPaging();
  };

  const handleTabChange = (event, value) => {
    setTab(value);
    setSearchTerm("");
    resetPaging();
  };

  const handleCourseChange = (event) => {
    const value = event.target.value;
    setCourseid(value);
    setTab("section");
    setSearchTerm("");
    resetPaging();

    dispatch({ type: examScheduleActions.SET_EXAM_SCHEDULE, payload: [] });
    dispatch({ type: examScheduleActions.SET_EXAM_PAPERS, payload: [] });
    dispatch({ type: examScheduleActions.SET_SCHEDULE_PREVIEW, payload: null });

    if (!value) return;
    dispatch({ type: examScheduleActions.GET_EXAM_SCHEDULE, payload: { courseid: value } });
    dispatch({ type: examScheduleActions.GET_SCHEDULE_PREVIEW, payload: { courseid: value } });

    const course = scheduleCourses?.find((item) => Number(item?.courseid) === Number(value));
    if (course?.usesPaperGrid) {
      dispatch({ type: examScheduleActions.GET_EXAM_PAPERS, payload: { courseid: value } });
    }
  };

  const handleAddRow = (method) => {
    dispatch({
      type: commonActions.SET_SHOW_MODAL,
      payload: { show: true, type: null, method: method, data: null },
    });
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "16px",
          marginTop: "24px",
          marginBottom: "14px",
        }}
      >
        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel id="schedule-course-label">Course</InputLabel>
          <Select
            labelId="schedule-course-label"
            label="Course"
            value={courseid}
            onChange={handleCourseChange}
            sx={{ height: "40px", borderRadius: "100px" }}
          >
            <MenuItem value={0}>Select Course</MenuItem>
            {scheduleCourses?.map((course) => (
              <MenuItem key={course?.courseid} value={course?.courseid}>
                {course?.coursename}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Searchbar handleSearch={handleSearch} value={searchTerm} />

        <Box sx={{ display: "flex", gap: "12px" }}>
          <MauvelousPrimaryButton
            title={"Add Schedule Row"}
            handleClick={() => handleAddRow("ESM")}
            disabled={!courseid}
          />
          {usesPaperGrid && (
            <MauvelousPrimaryButton
              title={"Add Paper"}
              handleClick={() => handleAddRow("EPM")}
              disabled={!courseid}
            />
          )}
        </Box>
      </Box>

      {selectedCourse && (
        <Typography
          sx={{
            fontSize: "13px",
            color: `${cssProperties?.color?.primary}`,
            marginBottom: "14px",
          }}
        >
          {selectedCourse?.coursename} uses the <strong>{selectedCourse?.layout}</strong>{" "}
          admit-card layout —{" "}
          {usesPaperGrid
            ? "section blocks and a paper-wise grid."
            : "section blocks only, no paper grid."}{" "}
          Date only prints as 5th April 2026, with weekday as 12th April 2026, Sunday; a
          note is appended after the value; an instruction row prints label only.
        </Typography>
      )}

      {courseid > 0 && (
        <Tabs
          value={tab}
          onChange={handleTabChange}
          sx={{ marginBottom: "14px", "& .MuiTab-root": { textTransform: "none" } }}
        >
          <Tab label={`Schedule Rows (${sectionRows?.length ?? 0})`} value="section" />
          {usesPaperGrid && <Tab label={`Papers (${paperRows?.length ?? 0})`} value="paper" />}
          <Tab label="Admit Card Preview" value="preview" />
        </Tabs>
      )}

      {tab === "preview" ? (
        <PreviewPanel preview={schedulePreview} />
      ) : (
        <>
          <DataTable
            keys={isPaperTab ? paperKeys : sectionKeys}
            values={courseid > 0 ? currentItems : []}
            page={isPaperTab ? "examPaperList" : "examScheduleList"}
          />
          <TablePagination
            pageCount={pageCount}
            handlePageChange={handlePageChange}
            currentPage={currentPage}
          />
        </>
      )}

      <SectionRowModal courseid={courseid} />
      <PaperRowModal courseid={courseid} />
      <DeleteModal courseid={courseid} />
    </>
  );
}
